import { useCallback, useEffect, useState } from 'react';
import { authHeader } from '../lib/session.js';

/* Cost observability, for administrators.
 *
 * What SentriPol's reviews cost over a window, by report section: reading
 * the policy documents and the AI analysis behind each part of the report. Every figure the backend could not
 * read arrives as null with a reason and is shown as "unknown" with that
 * reason — never as $0, which would claim nothing was spent.
 *
 * A page of its own, shown by the administrator's "Cost Observability" tab.
 * It stays mounted while the application tab is showing, so switching back
 * keeps the window chosen and the figures already loaded.
 */

const base = () => {
  const b = ((typeof window !== 'undefined' && window.SENTRIPOL_CONFIG) || {}).backend;
  return (b == null ? '' : b).replace(/\/+$/, '');
};

export async function fetchCosts(window_) {
  const res = await fetch(base() + '/api/costs?window=' + encodeURIComponent(window_), { headers: await authHeader() });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error('Your session has expired. Reload the page to sign in again.');
  if (res.status === 403) throw new Error('Cost figures are available to administrators only.');
  if (!res.ok) throw new Error(data.error || `Could not load costs (${res.status}).`);
  return data;
}

const money = (v, digits = 2) => (typeof v === 'number'
  ? '$' + v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: Math.max(digits, 4) })
  : 'unknown');
// Business figures: whole cents, and "<$0.01" rather than a run of decimals.
const cents = (v) => (typeof v !== 'number' ? 'unknown' : v > 0 && v < 0.005 ? '<$0.01'
  : '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const int = (v) => (typeof v === 'number' ? v.toLocaleString('en-US') : '—');
const WINDOWS = [['24h', 'Last 24 hours'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days']];

function Tile({ label, value, note, tone }) {
  return (
    <div className="sp-cost-tile">
      <div className="sp-cost-tile-label">{label}</div>
      <div className={'sp-cost-tile-value' + (tone ? ' ' + tone : '')}>{value}</div>
      {note ? <div className="sp-cost-tile-note">{note}</div> : null}
    </div>
  );
}

/* WHERE THE MONEY GOES — cost by report section, as a pie.
 *
 * Part-to-whole at a glance, five or six slices at most. Colour follows the
 * section, never its rank, so a section keeps its colour as figures change.
 * Identity is never colour alone: the legend beside the pie is the table, with
 * every section's name, activity, cost and share in text. Hover or keyboard
 * focus on a slice (or its legend row) lifts it and shows the detail. */
const SECTION_COLORS = {
  parsing: '#2a78d6',
  'analyze/profile': '#eb6834',
  'analyze/vertical': '#1baf7a',
  'analyze/locations': '#eda100',
  'bop/review': '#e87ba4',
  'other-ai': '#9a9a94'
};
const pct = (v) => (v >= 0.995 ? '100%' : v < 0.005 ? '<1%' : Math.round(v * 100) + '%');

function CostPie({ sections }) {
  const [hot, setHot] = useState(null);
  const [tip, setTip] = useState(null);
  const items = (sections || []).filter((x) => x.cost_usd > 0);
  if (!items.length) return <p className="sp-cost-empty">No costs recorded in this window.</p>;
  const R = 92, C = 110, LIFT = 6;
  let a0 = -Math.PI / 2;
  const slices = items.map((x) => {
    const a1 = a0 + x.share * 2 * Math.PI;
    const mid = (a0 + a1) / 2;
    const path = x.share >= 0.9999 ? null : [
      `M ${C} ${C}`,
      `L ${C + R * Math.cos(a0)} ${C + R * Math.sin(a0)}`,
      `A ${R} ${R} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${C + R * Math.cos(a1)} ${C + R * Math.sin(a1)}`,
      'Z'
    ].join(' ');
    const out = { ...x, path, mid };
    a0 = a1;
    return out;
  });
  const show = (x, ev) => {
    setHot(x.key);
    if (ev && ev.currentTarget && ev.currentTarget.ownerSVGElement) {
      const box = ev.currentTarget.ownerSVGElement.getBoundingClientRect();
      const px = ev.clientX != null ? ev.clientX - box.left : (C + R * 0.6 * Math.cos(x.mid)) * box.width / (2 * C);
      const py = ev.clientY != null ? ev.clientY - box.top : (C + R * 0.6 * Math.sin(x.mid)) * box.height / (2 * C);
      setTip({ x: px, y: py, item: x });
    }
  };
  const clear = () => { setHot(null); setTip(null); };
  return (
    <div className="sp-cost-pie">
      <div className="sp-cost-pie-plot" onMouseLeave={clear}>
        <svg viewBox={`0 0 ${2 * C} ${2 * C}`} role="img" aria-label="Cost by report section">
          {slices.map((x) => {
            const lift = hot === x.key ? LIFT : 0;
            const tx = lift * Math.cos(x.mid), ty = lift * Math.sin(x.mid);
            const common = {
              key: x.key, fill: SECTION_COLORS[x.key] || '#9a9a94', stroke: '#ffffff', strokeWidth: 2,
              transform: `translate(${tx} ${ty})`, tabIndex: 0, className: 'sp-cost-slice',
              opacity: hot && hot !== x.key ? 0.55 : 1,
              'aria-label': `${x.label}: ${cents(x.cost_usd)}, ${pct(x.share)} of total`,
              onMouseMove: (ev) => show(x, ev), onFocus: (ev) => show(x, ev), onBlur: clear
            };
            return x.path ? <path d={x.path} {...common} /> : <circle cx={C} cy={C} r={R} {...common} />;
          })}
        </svg>
        {tip ? (
          <div className="sp-cost-tip" style={{ left: tip.x, top: tip.y }} role="status">
            <strong>{tip.item.label}</strong>
            <span>{cents(tip.item.cost_usd)} · {pct(tip.item.share)} of total</span>
            <span className="sp-cost-tip-detail">{tip.item.detail}</span>
          </div>
        ) : null}
      </div>
      <table className="sp-cost-table sp-cost-legend">
        <thead><tr><th>Report section</th><th>Activity</th><th>Cost incurred</th><th>Share</th></tr></thead>
        <tbody>
          {items.map((x) => (
            <tr key={x.key} className={hot === x.key ? 'hot' : ''}
              onMouseEnter={() => setHot(x.key)} onMouseLeave={() => setHot(null)}>
              <td><span className="sp-cost-key" style={{ background: SECTION_COLORS[x.key] || '#9a9a94' }} />{x.label}</td>
              <td>{x.detail}</td>
              <td>{cents(x.cost_usd)}</td>
              <td>{pct(x.share)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CostDashboard({ active }) {
  const [win, setWin] = useState('7d');
  const [seen, setSeen] = useState(false);
  const [state, setState] = useState({ loading: false, data: null, error: null });

  // Loaded the first time the tab is opened, not at sign-in: an administrator
  // who only reviews policies never asks for the figures.
  useEffect(() => { if (active) setSeen(true); }, [active]);

  const load = useCallback(async (w) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try { setState({ loading: false, data: await fetchCosts(w), error: null }); }
    catch (e) { setState({ loading: false, data: null, error: e.message }); }
  }, []);

  useEffect(() => { if (seen) load(win); }, [seen, win, load]);

  const d = state.data;
  const m = d && d.model;
  const p = d && d.parsing;

  return (
    <main className="sp-cost-page" id="spCostPage" role="tabpanel" aria-labelledby="spTabCosts" hidden={!active}>
      <div className="sp-admin-card sp-cost-card">
        <h2 id="spCostTitle">Cost observability</h2>
        <div className="sp-admin-sub">What SentriPol’s reviews cost: reading the policy documents, and the AI analysis behind each section of the report.</div>

        <div className="sp-cost-windows" role="tablist">
          {WINDOWS.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={win === k}
              className={'sp-cost-window' + (win === k ? ' active' : '')} onClick={() => setWin(k)}>{l}</button>
          ))}
          <button className="sp-cost-window" onClick={() => load(win)} disabled={state.loading}>
            {state.loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {state.error ? <div className="sp-cost-alert">{state.error}</div> : null}

        {d ? (
          <>
            <div className="sp-cost-tiles">
              <Tile label="Total cost" value={money(d.total_usd)}
                note={d.complete ? 'Document reading + AI analysis' : 'Incomplete — see the note below'}
                tone={d.complete ? '' : 'warn'} />
              <Tile label="Reviews" value={int(d.reviews)}
                note={d.per_review_usd != null ? `${money(d.per_review_usd)} per review` : 'Cost per review unavailable'} />
              <Tile label="AI analysis" value={m && m.available ? cents(m.spend_usd) : 'unknown'}
                note={m && m.available ? `${int(m.calls)} AI call${m.calls === 1 ? '' : 's'}` : null} />
              <Tile label="Document reading" value={p && p.available ? money(p.est_usd) : 'unknown'}
                note={p && p.available ? `${int(p.billed_pages)} pages read` : null} />
            </div>

            <section className="sp-admin-section">
              <h3>Where the money goes</h3>
              <CostPie sections={d.sections} />
              {m && m.available && m.unpriced_calls > 0 ? (
                <div className="sp-cost-alert warn">
                  {m.unpriced_calls === 1 ? '1 AI call could not be priced, so its cost is'
                    : `${int(m.unpriced_calls)} AI calls could not be priced, so their cost is`} not included.
                </div>
              ) : null}
              {m && m.available && m.inexact_requests > 0 ? (
                <div className="sp-cost-alert warn">
                  {int(m.inexact_requests)} AI request{m.inexact_requests === 1 ? '' : 's'} did not report a cost, so the AI total is incomplete.
                </div>
              ) : null}
              {m && !m.available ? <div className="sp-cost-alert">AI analysis costs are unavailable right now.</div> : null}
              {p && !p.available ? <div className="sp-cost-alert">Document reading costs are unavailable right now.</div> : null}
            </section>

            <section className="sp-admin-section">
              <h3>By user</h3>
              {d.users && d.users.available ? (
                <>
                  <div className="sp-cost-scroll">
                    <table className="sp-cost-table">
                      <thead><tr>
                        <th>User</th><th>Reviews</th><th>Documents</th><th>Pages read</th>
                        <th>Document reading</th><th>AI analysis</th><th>Total cost</th>
                      </tr></thead>
                      <tbody>
                        {d.users.rows.length ? d.users.rows.map((r) => (
                          <tr key={r.user}>
                            <td>{r.user}</td><td>{int(r.reviews)}</td><td>{int(r.files)}</td><td>{int(r.billed_pages)}</td>
                            <td>{cents(r.parsing_usd)}</td>
                            <td title={r.ai_estimated_usd ? `Exact ${money(r.ai_exact_usd, 4)} + estimated ${money(r.ai_estimated_usd, 4)}` : 'Exact'}>
                              {cents(r.ai_usd)}{r.ai_estimated_usd ? ' *' : ''}
                            </td>
                            <td><strong>{cents(r.total_usd)}</strong></td>
                          </tr>
                        )) : <tr><td colSpan="7" className="sp-cost-empty">No activity in this window.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <p className="sp-cost-note">
                    A user is the access code, demo link or administrator a session was opened with; people sharing
                    one code are counted together.
                    {d.users.exact_only ? '' : ' * Part of this AI cost is estimated from the user’s share of requests.'}
                  </p>
                </>
              ) : <div className="sp-cost-alert">{(d.users && d.users.reason) || 'Per-user figures are unavailable.'}</div>}
            </section>

            <p className="sp-cost-note">
              Document reading is estimated at {p && p.usd_per_page ? money(p.usd_per_page, 4) : 'the configured rate'} per
              page read; files read before are free. AI analysis is the actual cost of each request.
              Updated {new Date(d.generated_at).toLocaleString()}.
            </p>
          </>
        ) : null}
      </div>
    </main>
  );
}
